import { Room } from '@spike/room-model';
import { Task } from '@spike/task-model';
import clsx from 'clsx';
import EmptyList from 'components/Bookings2/UI/EmptyList';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { Table } from 'components/UI/V3/Table/Table';
import { FunctionComponent, useMemo } from 'react';
import TasksTableRow from '../List/TasksTableRow';
import styles from './Dashboard.module.scss';
import { buildUnitIndex, incompleteTaskIds, taskRoomUnitId } from './grouping';
import { TableColumnSort } from 'utils/tableSort';

type EmptyVariant = 'upcoming' | 'completed' | 'all';

export type TasksSortField = 'pet_name' | 'task' | 'date' | 'room' | 'note';

interface TasksTablePagination {
    page: number;
    size: number;
    totalPages: number;
}

interface TasksTableProps {
    tasks: Array<Task>;
    rooms: Array<Room>;
    isLoading?: boolean;
    showLocationColumn?: boolean;
    showCompleteAll?: boolean;
    previewActions?: boolean;
    variant?: string;
    emptyVariant?: EmptyVariant;
    pagination?: TasksTablePagination;
    className?: string;
    sort?: TableColumnSort<TasksSortField>;
    onSort?: (field: TasksSortField) => void;
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
    onCompleteAll?: (taskIds: Array<number>) => void;
}

const TASKS_COLUMN_COUNT = 6;

export const TasksTable: FunctionComponent<TasksTableProps> = props => {
    const unitIndex = useMemo(() => buildUnitIndex(props.rooms), [props.rooms]);

    if (!props.isLoading && props.tasks.length === 0) {
        return <EmptyList variant={props.emptyVariant ?? 'upcoming'} />;
    }

    const allIncompleteIds = incompleteTaskIds(props.tasks);

    const roomLabel = (task: Task) => {
        const unitId = taskRoomUnitId(task);
        const ref = unitId != null ? unitIndex.get(unitId) : undefined;
        return ref ? { room: ref.roomName, unit: ref.unitName } : undefined;
    };

    return (
        <Table
            className={clsx(props.className, styles.table)}
            tableClassName={styles.tableFixed}
            fullHeight={true}
            pagination={props.pagination}
            onPageChange={page => props.onPageChange?.(page)}
            onSizeChange={size => props.onSizeChange?.(Number(size))}
        >
            <TableHeader className={styles.tableHead}>
                <TableHeaderCell
                    className={styles.colPet}
                    sortDirection={props.sort?.field === 'pet_name' ? props.sort.direction : undefined}
                    onSort={() => props.onSort?.('pet_name')}
                >
                    Pet Name
                </TableHeaderCell>
                <TableHeaderCell
                    className={styles.colTask}
                    sortDirection={props.sort?.field === 'task' ? props.sort.direction : undefined}
                    onSort={() => props.onSort?.('task')}
                >
                    Task
                </TableHeaderCell>
                <TableHeaderCell
                    className={styles.colDate}
                    sortDirection={props.sort?.field === 'date' ? props.sort.direction : undefined}
                    onSort={() => props.onSort?.('date')}
                >
                    Date &amp; Time
                </TableHeaderCell>
                <TableHeaderCell
                    className={styles.colRoom}
                    sortDirection={props.sort?.field === 'room' ? props.sort.direction : undefined}
                    onSort={() => props.onSort?.('room')}
                >
                    Room
                </TableHeaderCell>
                <TableHeaderCell
                    className={styles.colNotes}
                    sortDirection={props.sort?.field === 'note' ? props.sort.direction : undefined}
                    onSort={() => props.onSort?.('note')}
                >
                    Notes
                </TableHeaderCell>
                <TableHeaderCell
                    className={styles.colActions}
                    aria-label="Actions"
                >
                    {props.showCompleteAll && allIncompleteIds.length > 0 && (
                        <div className={styles.headerActions}>
                            <Button
                                label="Complete All"
                                variant="outlined"
                                size="small"
                                className={styles.completeAllButton}
                                onClick={() => props.onCompleteAll?.(allIncompleteIds)}
                            />
                        </div>
                    )}
                </TableHeaderCell>
            </TableHeader>
            <TableBody>
                {props.isLoading ? (
                    <TableRow>
                        <TableCell
                            colSpan={TASKS_COLUMN_COUNT}
                            className={styles.spinnerContainer}
                        >
                            <Spinner />
                        </TableCell>
                    </TableRow>
                ) : (
                    props.tasks.map(task => (
                        <TasksTableRow
                            key={task.id}
                            task={task}
                            roomLabel={roomLabel(task)}
                            showLocationColumn={props.showLocationColumn}
                            previewActions={props.previewActions}
                            variant={props.variant}
                        />
                    ))
                )}
            </TableBody>
        </Table>
    );
};

export default TasksTable;
